import { useCallback, useEffect, useRef, useState } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { FallbackButton, Screen } from "@/components/Screen";
import { Button } from "@/components/Button";
import { Note } from "@/components/Note";
import { Spinner } from "@/components/Spinner";
import { agreementDownloadUrl, brokerApi, friendlyError, type SignContext, type SigningDocument } from "@/lib/api";
import { openExternal, platform } from "@/telegram/tg";

type Props = { onDone: () => void };

type DocView = { id: string; name: string | null; isPdf: boolean; b64: string; mime: string; src?: string; srcDoc?: string };

type Stage =
  | { kind: "loading" }
  | { kind: "onboarding" }
  | { kind: "document"; docs: DocView[] }
  | { kind: "otp" }
  | { kind: "signed" }
  | { kind: "view"; docs: DocView[]; signedAt: string | null }   // просмотр подписанного ДБО
  | { kind: "error"; message: string };

function b64ToUtf8(b64: string): string {
  try {
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return new TextDecoder("utf-8").decode(bytes);
  } catch {
    return "";
  }
}

function toDocView(d: SigningDocument): DocView {
  const mime = (d.document_content_type ?? "").split(";")[0].trim() || "application/octet-stream";
  const isPdf = mime.toLowerCase().includes("pdf");
  return {
    id: d.document_id,
    name: d.file_name,
    isPdf,
    b64: d.document_content_base64,
    mime,
    ...(isPdf
      ? { src: `data:application/pdf;base64,${d.document_content_base64}` }
      : { srcDoc: b64ToUtf8(d.document_content_base64) }),
  };
}

async function downloadDoc(doc: DocView): Promise<void> {
  // В Telegram/WWChat WebView программная blob/data-загрузка через <a download> молча не
  // срабатывает (кнопка «ничего не делает»). Поэтому во встроенном браузере берём короткоживущий
  // токен и открываем прямую ссылку системным браузером (openLink) — там attachment качается штатно.
  if (platform() !== "web") {
    try {
      const { token } = await brokerApi.downloadToken();
      openExternal(agreementDownloadUrl(token, doc.id || undefined));
      return;
    } catch {
      /* токен не выдан — падаем на blob-фоллбэк ниже */
    }
  }
  try {
    const bytes = Uint8Array.from(atob(doc.b64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: doc.mime }));
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.name || (doc.isPdf ? "agreement.pdf" : "agreement.html");
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  } catch {
    /* если webview блокирует загрузку — документ всё равно виден в просмотре выше */
  }
}

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

export function Agreement({ onDone }: Props) {
  const [stage, setStage] = useState<Stage>({ kind: "loading" });
  const [otp, setOtp] = useState("");
  const [relayedCode, setRelayedCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inlineErr, setInlineError] = useState<string | null>(null);
  const started = useRef(false);
  // контекст подписи и otp_id держим на фронте — бэкенд stateless, ничего не теряется
  const signCtx = useRef<SignContext | null>(null);
  const otpId = useRef<string>("");

  // Загрузить документ оферты (онбординг уже выполнен)
  const loadDocument = useCallback(async () => {
    const pkg = await brokerApi.signingStart();
    if (pkg.already_signed) {
      setStage({ kind: "signed" });
      return;
    }
    if (!pkg.sign_context) {
      setStage({ kind: "error", message: "Не удалось получить документы. Попробуйте ещё раз." });
      return;
    }
    signCtx.current = pkg.sign_context;
    setStage({ kind: "document", docs: (pkg.documents ?? []).map(toDocView) });
  }, []);

  // Просмотр уже подписанного ДБО (read-only)
  const loadSigned = useCallback(async () => {
    try {
      const a = await brokerApi.agreement();
      setStage({ kind: "view", docs: (a.documents ?? []).map(toDocView), signedAt: a.signed_at });
    } catch {
      // не смогли подтянуть документ — показываем факт подписания без просмотра
      setStage({ kind: "signed" });
    }
  }, []);

  // Точка входа: проверяем статус → онбордим при необходимости → грузим документ
  const boot = useCallback(async () => {
    try {
      const st = await brokerApi.status();
      if (!st.active) {
        onDone(); // брокерская интеграция выключена — пропускаем
        return;
      }
      if (st.agreement_signed) {
        await loadSigned();   // подписан → показываем сам документ (read-only) + скачивание
        return;
      }
      if (!st.onboarded) {
        setStage({ kind: "onboarding" });
        await brokerApi.onboard();
      }
      await loadDocument();
    } catch (e) {
      setStage({ kind: "error", message: friendlyError(e, "Не удалось подготовить соглашение. Напишите в поддержку.") });
    }
  }, [onDone, loadDocument, loadSigned]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void boot();
  }, [boot]);

  const requestOtp = useCallback(async () => {
    if (busy || !signCtx.current) return;
    setBusy(true);
    try {
      const r = await brokerApi.sendOtp(signCtx.current);
      otpId.current = r.otp_id;
      setOtp("");
      setRelayedCode(null);
      setStage({ kind: "otp" });
    } catch (e) {
      setStage({ kind: "error", message: friendlyError(e, "Не удалось отправить код. Попробуйте ещё раз.") });
    } finally {
      setBusy(false);
    }
  }, [busy]);

  // Код приходит на нашу прокси-почту → показываем его прямо здесь (надёжнее, чем ждать
  // письмо/сообщение бота). Пуллим, пока на экране ввода кода.
  useEffect(() => {
    if (stage.kind !== "otp") return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await brokerApi.otpCode();
        if (alive && r.code) setRelayedCode(r.code);
      } catch {
        /* ignore */
      }
    };
    void tick();
    const t = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [stage.kind]);

  const confirm = useCallback(async () => {
    const code = otp.trim();
    if (code.length < 4 || busy || !signCtx.current || !otpId.current) return;
    setBusy(true);
    try {
      const r = await brokerApi.signingConfirm(signCtx.current, otpId.current, code);
      if (r.signed) setStage({ kind: "signed" });
      else setStage({ kind: "error", message: "Подпись не подтверждена. Попробуйте ещё раз." });
    } catch (e) {
      // неверный код приходит как 400 — даём повторить
      setOtp("");
      setStage({ kind: "otp" });
      setInlineError(friendlyError(e, "Неверный код. Проверьте письмо и попробуйте снова."));
    } finally {
      setBusy(false);
    }
  }, [otp, busy]);

  useBackButton(onDone);

  const mainBtn = ((): { text: string; onClick: () => void; active: boolean; visible: boolean; loading: boolean } => {
    // на загрузке — нативный спиннер Telegram (loading), текст кнопки не меняем,
    // иначе при смене текста на «Готово» остаётся визуальный «хвост»
    if (stage.kind === "document")
      return { text: "Подписать", onClick: requestOtp, active: !busy, visible: true, loading: busy };
    if (stage.kind === "otp")
      return { text: "Подтвердить", onClick: confirm, active: otp.trim().length >= 4 && !busy, visible: true, loading: busy };
    if (stage.kind === "signed" || stage.kind === "view")
      return { text: "Готово", onClick: onDone, active: true, visible: true, loading: false };
    return { text: "", onClick: () => {}, active: false, visible: false, loading: false };
  })();
  useMainButton(mainBtn);

  return (
    <Screen
      title={
        stage.kind === "signed" || stage.kind === "view"
          ? "Договор с брокером (ДБО)"
          : stage.kind === "otp"
          ? "Подтверждение по email"
          : stage.kind === "error"
          ? "Не получилось"
          : "Документы на подпись"
      }
      subtitle={
        stage.kind === "document"
          ? `Ознакомьтесь с документами (${stage.docs.length}). Для подписания отправим код на ваш email.`
          : stage.kind === "view"
          ? (stage.signedAt ? `Подписан ${fmtDate(stage.signedAt)}` : "Подписан")
          : stage.kind === "onboarding"
          ? "Регистрируем вас у брокера…"
          : undefined
      }
    >
      {(stage.kind === "loading" || stage.kind === "onboarding") && (
        <div className="flex flex-col items-center gap-3 py-16 text-white/45">
          <Spinner className="h-7 w-7" />
          <div className="text-[14px]">{stage.kind === "onboarding" ? "Создаём профиль у брокера…" : "Загрузка…"}</div>
        </div>
      )}

      {(stage.kind === "document" || stage.kind === "view") && (
        <div className="space-y-3">
          {stage.kind === "view" && (
            <Note>
              Ваш договор с брокером подписан{stage.signedAt ? ` ${fmtDate(stage.signedAt)}` : ""}. Ниже — сами
              документы, их можно скачать.
            </Note>
          )}
          {stage.docs.map((doc, i) => (
            <div key={doc.id} className="overflow-hidden rounded-2xl border border-white/10 bg-white">
              <div className="flex items-center justify-between border-b border-black/10 bg-black/5 px-3 py-2">
                <span className="text-[13px] font-medium text-black/50">📄 {doc.name || `Документ ${i + 1}`}</span>
                <button
                  onClick={() => void downloadDoc(doc)}
                  className="rounded-lg bg-black/5 px-2.5 py-1 text-[12px] font-medium text-black/60 active:bg-black/10"
                >
                  ↓ Скачать
                </button>
              </div>
              <iframe
                title={doc.name || `Документ ${i + 1}`}
                {...(doc.isPdf ? { src: doc.src } : { srcDoc: doc.srcDoc })}
                sandbox=""
                className="h-[45vh] w-full"
              />
            </div>
          ))}
        </div>
      )}

      {stage.kind === "otp" && (
        <div className="space-y-3">
          {relayedCode ? (
            <button
              type="button"
              onClick={() => { setOtp(relayedCode); setInlineError(null); }}
              className="w-full rounded-xl border border-brand/40 bg-brand/10 px-4 py-3 text-center active:opacity-80"
            >
              <div className="text-[12px] text-white/60">Ваш код подтверждения</div>
              <div className="mt-0.5 text-[24px] font-semibold tracking-[0.3em] text-brand">{relayedCode}</div>
              <div className="mt-0.5 text-[12px] text-white/45">Нажмите, чтобы подставить</div>
            </button>
          ) : (
            <Note>Запросили код. Он появится здесь через несколько секунд (и продублируется в Telegram).</Note>
          )}
          <input
            inputMode="numeric"
            autoFocus
            value={otp}
            onChange={(e) => {
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 8));
              setInlineError(null);
            }}
            placeholder="Код из письма"
            className="w-full rounded-xl border border-white/[0.08] bg-white/[0.06] px-4 py-3.5 text-center text-[20px] tracking-[0.3em] text-white outline-none placeholder:text-white/30 focus:border-white/20 [color-scheme:dark]"
          />
          {inlineErr && <div className="text-[13px] text-red-400">{inlineErr}</div>}
          <button onClick={requestOtp} disabled={busy} className="w-full py-2 text-center text-[13px] text-brand active:opacity-70">
            Отправить код повторно
          </button>
        </div>
      )}

      {stage.kind === "signed" && (
        <div className="rounded-2xl bg-emerald-500/10 p-5 text-center">
          <div className="text-3xl">✅</div>
          <div className="mt-2 text-[15px]">Договор-оферта подписан. Можно продолжать.</div>
        </div>
      )}

      {stage.kind === "error" && (
        <div className="space-y-3">
          <Note variant="danger">{stage.message}</Note>
          <Button
            onClick={() => {
              started.current = false;
              setStage({ kind: "loading" });
              void boot();
            }}
          >
            Попробовать снова
          </Button>
        </div>
      )}

      {stage.kind === "document" && <FallbackButton text="Подписать" onClick={requestOtp} loading={busy} />}
      {stage.kind === "otp" && (
        <FallbackButton text="Подтвердить" onClick={confirm} disabled={otp.trim().length < 4} loading={busy} />
      )}
      {stage.kind === "signed" && <FallbackButton text="Готово" onClick={onDone} />}
    </Screen>
  );
}
